import React from 'react';
import { Construction, Eye, FileEdit, Lock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { usePublicSettings } from '../hooks/usePublicSettings';

/**
 * Wraps a public page and respects the admin's visibility setting.
 * - "published" (default): renders children
 * - "draft": shows "yapım aşamasında" card to public; admins still see content with banner
 * - "maintenance": shows "bakım modunda" card to everyone (admin sees a banner with "yine de göster" link)
 *
 * The site-wide maintenance is also respected via the `site_maintenance_mode` setting.
 */
export default function PageGate({ slug, children }) {
  const s = usePublicSettings();
  const status = s[`page_visibility_${slug}`] || 'published';
  const siteMaint = s.site_maintenance_mode === '1' || s.site_maintenance_mode === 'true';
  const isAdmin = typeof window !== 'undefined' && !!localStorage.getItem('yekuv_token');
  const bypass = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('preview') === '1';

  // Resolve effective state
  let state = status;
  if (siteMaint && !bypass) state = 'maintenance';

  // Admins always see content (with a banner) unless they explicitly opt-out.
  // Public users see placeholder for draft/maintenance.
  if (state === 'published' || isAdmin) {
    return (
      <>
        {(state !== 'published' || siteMaint) && isAdmin && (
          <AdminBanner state={state} siteMaint={siteMaint} slug={slug} />
        )}
        {children}
      </>
    );
  }

  // Public users see the appropriate placeholder
  if (state === 'maintenance') {
    return (
      <PlaceholderCard
        icon={Construction}
        kicker="Bakım Modu"
        title={s[`page_maintenance_title_${slug}`] || s.site_maintenance_title || 'Çok yakında geri döneceğiz'}
        text={s[`page_maintenance_text_${slug}`] || s.site_maintenance_text || 'Bu sayfa şu an bakımda. En kısa sürede yenilenmiş hâliyle sizinle olacağız. Anlayışınız için teşekkür ederiz.'}
        isAdmin={isAdmin}
        slug={slug}
      />
    );
  }

  // draft
  return (
    <PlaceholderCard
      icon={FileEdit}
      kicker="Yapım Aşamasında"
      title="Bu içerik yakında yayında"
      text="Bu sayfa şu an düzenleniyor. En kısa sürede yayınlayacağız. Vakfımızın diğer içeriklerini gezmeye devam edebilirsiniz."
      isAdmin={isAdmin}
      slug={slug}
    />
  );
}

function AdminBanner({ state, siteMaint, slug }) {
  const labels = {
    draft: { txt: 'Bu sayfa TASLAK durumda — sadece admin olarak görüyorsunuz.', cls: 'bg-coral-300 text-cream' },
    maintenance: { txt: 'Bu sayfa BAKIM MODUNDA — sadece admin olarak görüyorsunuz.', cls: 'bg-gold text-ink-700' },
    published: { txt: 'Site BAKIM MODUNDA — sadece admin olarak görüyorsunuz.', cls: 'bg-gold text-ink-700' },
  };
  const meta = labels[state] || labels.published;
  return (
    <div className={`${meta.cls} px-4 py-2.5 text-sm font-semibold flex items-center gap-3 sticky top-0 z-50`}>
      <Lock className="w-4 h-4 shrink-0" />
      <span className="flex-1">{meta.txt}</span>
      <Link to={`/admin/visibility`} className="underline decoration-2 underline-offset-2 hover:opacity-90">
        Görünürlüğü Yönet
      </Link>
    </div>
  );
}

function PlaceholderCard({ icon: Icon, kicker, title, text, isAdmin, slug }) {
  return (
    <section className="min-h-[60vh] bg-cream grid place-items-center px-6 py-24">
      <div className="max-w-xl w-full text-center">
        <div className="w-16 h-16 rounded-2xl bg-ink-50 text-ink-500 grid place-items-center mx-auto">
          <Icon className="w-7 h-7" />
        </div>
        <div className="text-[11px] tracking-[0.3em] uppercase text-coral-300 font-bold mt-6">{kicker}</div>
        <h1 className="font-display text-4xl md:text-5xl mt-3 leading-[1.1] text-ink-700">{title}</h1>
        <p className="mt-5 text-ink-500 leading-relaxed">{text}</p>

        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Link to="/" className="inline-flex items-center gap-2 bg-ink-600 text-cream rounded-full px-6 h-11 text-sm font-semibold hover:bg-ink-500">
            <Eye className="w-4 h-4" /> Anasayfa
          </Link>
          <Link to="/iletisim" className="inline-flex items-center gap-2 bg-cream-200 text-ink-700 rounded-full px-6 h-11 text-sm font-semibold border border-ink-100 hover:border-ink-300">
            İletişim
          </Link>
          {isAdmin && (
            <a href={`?preview=1`} className="inline-flex items-center gap-2 bg-gold text-ink-700 rounded-full px-6 h-11 text-sm font-semibold hover:bg-gold-400">
              <Lock className="w-4 h-4" /> Admin önizleme
            </a>
          )}
        </div>
      </div>
    </section>
  );
}
